import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, CardBody, Col, Container, Row, Spinner } from 'reactstrap';
import SimpleCard from '../../../utils/card/SimpleCard';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { getRegs } from '../../../utils/fc';
import { fmtDateBr, startOfToday } from '../../../utils/monthRef';
import KanbanColumn from './KanbanColumn';
import {
  DASHBOARD_ENTREGAS_DATE_CUTOFF,
  DASHBOARD_ENTREGAS_FIELDS,
  KANBAN_COLUMNS,
  buildEntregasSlices,
  fetchCoordinatedProjIds,
  filterProductsForAccess,
  prodStatusLabel,
  resolveEntregasAccess,
  slicesByView,
  statusId,
  toDateInputValue,
} from './dashboardEntregasHelpers';

const StatusPill = ({ value }) => {
  const id = Number(value) || 0;
  const bg = id === 8
    ? '#5664d2'
    : id === 7
      ? '#1cbb8c'
      : id === 6
        ? '#f1b44c'
        : id === 9
          ? '#ff5757'
          : '#4e515e';
  return (
    <span
      style={{
        fontSize: 11,
        padding: '2px 8px',
        borderRadius: 999,
        color: '#fff',
        backgroundColor: bg,
        whiteSpace: 'nowrap',
      }}
    >
      {prodStatusLabel(id) || '—'}
    </span>
  );
};

const DetailsCard = ({ product, onClose }) => {
  if (!product) return null;
  const LabelValue = ({ label, value }) => (
    <div style={{ display: 'flex', gap: 6, minWidth: 0 }}>
      <span style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>{label}:</span>
      <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value ?? '-'}</span>
    </div>
  );

  return (
    <SimpleCard
      standard={false}
      title={(
        <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
          <h4 className="text-truncate font-size-14 mb-0">{`Detalhes: ${product.prod_desc || 'Produto'}`}</h4>
          {typeof onClose === 'function' ? (
            <button
              type="button"
              className="btn btn-sm btn-link"
              onClick={onClose}
              style={{ padding: 0, fontSize: 12 }}
            >
              Fechar
            </button>
          ) : null}
        </div>
      )}
      text={(
        <div className="d-flex flex-column" style={{ gap: 10, fontSize: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#6c757d', fontSize: 12 }}>Projeto</div>
              <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {product.proj_nome || '-'}
              </div>
            </div>
            <StatusPill value={statusId(product)} />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <LabelValue label="Data interna" value={fmtDateBr(product.prod_dtInterna)} />
            <LabelValue label="Faturamento inicial" value={fmtDateBr(product.prod_dtLimAprov)} />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <LabelValue label="Entrega (limite)" value={fmtDateBr(product.prod_dtEntrega)} />
            <LabelValue label="Entregue ao cliente" value={fmtDateBr(product.prodEntregueAoClienteData)} />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <LabelValue label="Aprovado em" value={fmtDateBr(product.prodAprovacaoData)} />
            <LabelValue
              label="Aut. faturamento"
              value={fmtDateBr(product.prodAutFatData || product.prod_dtAutFat)}
            />
          </div>
          <div>
            Coordenador:{' '}
            <b>{product.prod_responsavelNome ?? product.prod_responsavelNomeAtual ?? '—'}</b>
          </div>
          <div className="d-flex" style={{ gap: 16 }}>
            {product.proj_id ? (
              <Link to={`/dep-tecnico/projetos/${product.proj_id}`}>Abrir projeto</Link>
            ) : null}
            {product.prod_drive ? (
              <a target="_blank" rel="noreferrer" href={product.prod_drive}>
                Pasta do produto
              </a>
            ) : null}
          </div>
        </div>
      )}
    />
  );
};

class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="page-content">
          <Container fluid>
            <Card>
              <CardBody>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Não foi possível montar o dashboard.</div>
                <div style={{ fontSize: 13, color: '#6c757d', whiteSpace: 'pre-wrap' }}>
                  {String(this.state.error?.message || this.state.error)}
                </div>
              </CardBody>
            </Card>
          </Container>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function DashboardEntregasPage() {
  return (
    <DashboardErrorBoundary>
      <DashboardEntregas />
    </DashboardErrorBoundary>
  );
}

function DashboardEntregas() {
  const user = useSelector((state) => state.auth && state.auth.user);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [access, setAccess] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProd, setSelectedProd] = useState(null);
  const [dateFrom, setDateFrom] = useState(toDateInputValue(DASHBOARD_ENTREGAS_DATE_CUTOFF));
  const [dateTo, setDateTo] = useState('');

  const today = useMemo(() => startOfToday(), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [raw, coordIds] = await Promise.all([
          getRegs('prod', { prod_ativo: 1 }, DASHBOARD_ENTREGAS_FIELDS),
          fetchCoordinatedProjIds(user?.pf_id),
        ]);
        if (cancelled) return;
        const nextAccess = resolveEntregasAccess(user, coordIds);
        setAccess(nextAccess);
        setProducts(filterProductsForAccess(Array.isArray(raw) ? raw : [], user, nextAccess));
      } catch (err) {
        console.error('[DashboardEntregas] falha ao carregar', err);
        setLoadError(err);
        setAccess(resolveEntregasAccess(user, []));
        setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user]);

  const slices = useMemo(
    () => buildEntregasSlices(products, today, { dateFrom, dateTo }),
    [products, today, dateFrom, dateTo],
  );

  useEffect(() => {
    if (!selectedProd?.prod_id) return;
    const updated = (products || []).find((p) => Number(p.prod_id) === Number(selectedProd.prod_id));
    if (updated) setSelectedProd(updated);
  }, [products, selectedProd?.prod_id]);

  const boardEmpty = !slices.aEntregar.length
    && !slices.aLiberar.length;

  if (loading) {
    return (
      <div className="page-content">
        <div className="centered">
          <Spinner />
        </div>
      </div>
    );
  }

  if (access && !access.canAccess) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard de entregas" breadcrumbItems={[]} />
          <Card>
            <CardBody>
              Este dashboard é restrito à direção/gerência técnica e aos coordenadores dos projetos.
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Dashboard de entregas" breadcrumbItems={[]} />
        {!access?.seeAll && (
          <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 12 }}>
            Exibindo apenas produtos dos projetos que você coordena.
          </div>
        )}

        <div
          className="d-flex align-items-end justify-content-between"
          style={{ gap: 12, flexWrap: 'wrap', marginBottom: 16 }}
        >
          <div>
            <h3 style={{ marginBottom: 4 }}>Entregas</h3>
            <div style={{ fontSize: 12, color: '#6c757d' }}>
              Cada card mostra entrega, aprovação e faturar. Após entregar, o fluxo vai direto para liberar p/ faturamento (sem coluna intermediária).
            </div>
          </div>
          <div className="d-flex align-items-end" style={{ gap: 10, flexWrap: 'wrap' }}>
            <label style={{ marginBottom: 0, fontSize: 12, color: '#6c757d' }}>
              De
              <input
                type="date"
                min={toDateInputValue(DASHBOARD_ENTREGAS_DATE_CUTOFF)}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value || toDateInputValue(DASHBOARD_ENTREGAS_DATE_CUTOFF))}
                style={{ display: 'block', marginTop: 4, padding: '6px 10px', borderRadius: 6, border: '1px solid #ced4da' }}
              />
            </label>
            <label style={{ marginBottom: 0, fontSize: 12, color: '#6c757d' }}>
              Até
              <input
                type="date"
                min={dateFrom || toDateInputValue(DASHBOARD_ENTREGAS_DATE_CUTOFF)}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value || '')}
                style={{ display: 'block', marginTop: 4, padding: '6px 10px', borderRadius: 6, border: '1px solid #ced4da' }}
              />
            </label>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                setDateFrom(toDateInputValue(DASHBOARD_ENTREGAS_DATE_CUTOFF));
                setDateTo('');
              }}
            >
              Padrão (≥ 01/06/2026)
            </button>
          </div>
        </div>

        {loadError ? (
          <Card className="mb-3">
            <CardBody>
              <div style={{ fontWeight: 700, color: '#ff5757', marginBottom: 4 }}>
                Não foi possível carregar os produtos.
              </div>
              <div style={{ fontSize: 13, color: '#6c757d' }}>
                Verifique a conexão e tente novamente. Detalhe no console:
                {' '}
                <code>[DashboardEntregas] falha ao carregar</code>
              </div>
            </CardBody>
          </Card>
        ) : null}

        {boardEmpty && !loadError ? (
          <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 12 }}>
            Nenhum produto nas duas colunas para o filtro de datas atual.
          </div>
        ) : null}

        <Row className="align-items-stretch">
          {KANBAN_COLUMNS.map((viewKey) => (
            <Col
              key={viewKey}
              xs={12}
              lg={6}
              className="mb-3"
              style={{ display: 'flex' }}
            >
              <div style={{ width: '100%' }}>
                <KanbanColumn
                  viewKey={viewKey}
                  items={slicesByView(slices, viewKey)}
                  today={today}
                  selectedProdId={selectedProd?.prod_id}
                  onSelectProduct={(product) => setSelectedProd(product)}
                />
              </div>
            </Col>
          ))}
        </Row>

        {selectedProd ? (
          <Row className="mt-1">
            <Col xs={12} lg={8} xl={6}>
              <DetailsCard
                product={selectedProd}
                onClose={() => setSelectedProd(null)}
              />
            </Col>
          </Row>
        ) : null}
      </Container>
    </div>
  );
}
